import React, { FC, useState, useEffect, useRef, ChangeEvent } from 'react';
import useDidUpdate from '../../../../../../../hooks/useDidUpdate.effect';
import { documentService } from '../../../../../../../utils/window.utils.js';
import Icon from '../../../../../../@shared/icon';
import HiglightedOption from '../../../../../../@shared/highlightedOption';
import DictionaryTerm from '../../../../../../@shared/dictionaryTerm';
import { iconSize } from '../../../../../../../constants/icon.constants';
import { TDropDown, TDropDownOption } from '../../../../../types/dynamicDropDown.types';

const DropDown: FC<TDropDown> = ({
  dropdownOptions = [],
  id,
  fields,
  label,
  isEnabled,
  selectedValue,
  setSelectedValue,
  dropdownIndex

}) => {
  const { field, tracker } = fields;
  const hasAnyError = !!fields?.errors?.length;
  const [isVisible, setIsVisible] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [hasTextInputFocus, setHasTextInputFocus] = useState(false);
  const [initialTextValue, setInitialTextValue] = useState('');
  const [textValue, setTextValue] = useState(selectedValue[dropdownIndex]?.text ?? '');
  const dropdownRef: React.MutableRefObject<HTMLUListElement | null> = useRef(null);
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  useEffect(() => {
    if (!fields.value || !selectedValue[dropdownIndex] || selectedValue[dropdownIndex]?.itemId === '') {
      setTextValue('');
      setInitialTextValue('');
    }
  }, [fields.value, selectedValue]);

  const onDropdownFocus = (): void => {
    setHasFocus(true);
    setHasTextInputFocus(true);
  };

  const onTextInputBlur = (): void => {
    setHasTextInputFocus(false);
  };

  const onDropdownClick = (): void => {
    setIsVisible(!isVisible);

    if (!isVisible) {
      inputRef?.current?.focus();
    } else {
      inputRef?.current?.blur();
    }
  };

  const onSelectOption = (selectedOption: TDropDownOption): void => {
    setTextValue(selectedOption.text);
    setInitialTextValue(selectedOption.text);
    setSelectedValue((prevState) => {
      const newState = [...prevState];
      if (prevState[dropdownIndex]?.itemId === selectedOption.itemId) {
        return prevState;
      }
      newState[dropdownIndex] = selectedOption;
      return newState.slice(0, dropdownIndex + 1);
    })
    setIsVisible(false);
  };

  const onInputUpdate = (e: ChangeEvent<HTMLInputElement>): void => {
    setTextValue(e?.target?.value);
  };

  const handleClickOutside = (event): void => {
    if (dropdownRef?.current?.contains(event?.target)) {
      return;
    }

    setIsVisible(false);

    if (textValue === '') {
      setTextValue('');
    } else {
      const hasMatch = dropdownOptions.some((opt) => opt.text === textValue);

      if (!hasMatch) {
        setTextValue(initialTextValue);
      }
    }

    setHasFocus(false);
  };

  useEffect(() => {
    documentService()?.addEventListener('mouseup', handleClickOutside);
    return (): void => {
      documentService()?.removeEventListener('mouseup', handleClickOutside);
    };
  }, [initialTextValue, textValue]); // eslint-disable-line

  useDidUpdate(() => {
    if (hasFocus) {
      tracker?.onFocusField(field, textValue);
    } else {
      tracker?.onBlurField(field, textValue);
    }
  }, [hasFocus]);

  return (
    <>
      <ul className={`dynamic-dropdown ${hasAnyError ? 'dynamic-dropdown--has-error' : ''}`} ref={dropdownRef}>
        <li
          className={`dynamic-dropdown__input-container ${hasFocus ? 'dynamic-dropdown__input-container--has-focus' : ''} ${
            !isEnabled ? 'dynamic-dropdown__input-container--disable' : ''
          }`}
        >
          <label
            htmlFor={id}
            className={`dynamic-dropdown__label ${!isEnabled ? 'dynamic-dropdown__label--disable' : ''} ${
              (!hasTextInputFocus && textValue === '' && !hasFocus)
                ? 'dynamic-dropdown__label--empty-field'
                : ''
            }`}
          >
            <DictionaryTerm keyword={label} />
          </label>
          <input
            className={`dynamic-dropdown__text-input ${!isEnabled ? 'dynamic-dropdown__text-input--disable' : ''}`}
            type="text"
            value={textValue}
            onChange={onInputUpdate}
            ref={inputRef}
            id={id}
            onClick={onDropdownClick}
            onFocus={onDropdownFocus}
            onBlur={onTextInputBlur}
            autoComplete="off"
            form="novalidatedform"
          />
          <Icon
            iconName={isVisible ? 'arrow-chevron-up' : 'arrow-chevron-down'}
            size={iconSize.dsk}
            extraClassName="dynamic-dropdown__icon"
            color="gray"
            onClick={onDropdownClick}
          />
        </li>

        <li className={`dynamic-dropdown__list-container ${isVisible ? 'dynamic-dropdown__list-container--visible' : ''}`}>
          <ul className="dynamic-dropdown__list" role="listbox">
            <>
              {dropdownOptions.map((item) => {
                return (
                  <li
                    className="dynamic-dropdown__list-item"
                    role="option" // eslint-disable-line
                    onClick={(): void => {
                      onSelectOption(item);
                    }}
                    key={item.value}
                  >
                    <HiglightedOption keyword={textValue} optionText={item.text} key={item.itemId} />
                  </li>
                );
              })}
            </>
          </ul>
        </li>
      </ul>
    </>
  );
};

export default DropDown;
